import React from "react";
import { Link } from "react-router-dom";
import { Shield, TrendingUp, Trophy, User as UserSilhouette } from "lucide-react";
import ReliabilityBadge from "./ReliabilityBadge";
import AdminRemovePlayerButton from "./AdminRemovePlayerButton";
import { useT } from "../context/LanguageContext";
import { getSkin, SkinPatternOverlay } from "../v2/skins";

/**
 * eFootball-card inspired player widget. Dark stadium gradient, bold OVR-style
 * score (reliability) top-left, win-rate pill top-right, club-style avatar
 * center (silhouette when no upload), stats bar footer, follow button.
 *
 * Iter 40: respects `p.active_skin` and re-themes the card backdrop + rim
 * accordingly (Bronze / Elite / Legend / Phantom). All metrics remain visible.
 */
export default function SocialCard({ p, onFollow, viewer, showMessageButton = false, onMessage, onRemoved, linkPrefix = "/players/" }) {
  const { t } = useT();
  const overall = Number(p.reliability ?? 70);
  const rel = overall;
  const skin = getSkin(p.active_skin);
  // Dynamic rim gradient — higher score = hotter rim (classic FUT rarity vibe)
  // Skinned cards override the rim with their accent so the theme reads instantly.
  const rim = skin
    ? null
    : (rel >= 85
      ? "bg-gradient-to-b from-[#FFD700] via-[#7C3AED] to-[#00FF87]"
      : rel >= 70
        ? "bg-gradient-to-b from-[#7C3AED] via-[#22D3EE] to-[#00FF87]"
        : rel >= 50
          ? "bg-gradient-to-b from-[#FFCC00] via-[#7C3AED] to-[#64748B]"
          : "bg-gradient-to-b from-[#E11D48] via-[#7C3AED] to-[#64748B]");

  const club = (p.current_league?.name || p.platform || "UNRANKED").toUpperCase();
  const isFriend = Boolean(p.is_friend);
  const isFollowing = Boolean(p.is_following);
  const me = viewer && viewer !== false ? viewer.id : null;
  const isSelf = p.id === me;

  const handleFollow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onFollow) onFollow(p);
  };

  return (
    <div
      className="relative rounded-[6px] p-[2px] group match-card-lift"
      data-testid={`social-card-${p.id}`}
    >
      {/* Card rim — skin-tinted for skinned cards */}
      {skin ? (
        <div
          className="absolute inset-0 rounded-[6px] opacity-95 pointer-events-none"
          aria-hidden
          style={{ background: `linear-gradient(180deg, ${skin.accent} 0%, ${skin.border} 50%, ${skin.accent} 100%)` }}
        />
      ) : (
        <div className={`absolute inset-0 rounded-[6px] opacity-90 ${rim} pointer-events-none`} aria-hidden />
      )}
      {/* Inner card */}
      <div className="relative rounded-[4px] overflow-hidden bg-[#0A0A0A] border border-black"
           data-skin={p.active_skin || "default"}>
        {/* Admin-only remove button, top-left corner */}
        <div className="absolute top-2 left-2 z-20">
          <AdminRemovePlayerButton
            userId={p.id}
            username={p.username}
            targetRole="player"
            size="xs"
            testId={`social-card-remove-${p.id}`}
            onRemoved={onRemoved}
            className="bg-black/60 backdrop-blur-sm"
          />
        </div>
        {/* Abstract EA-FC style backdrop. When the player has a skin applied
            we paint that skin's gradient instead, then layer the existing
            radial highlights at reduced opacity so the card still feels FUT-y. */}
        {skin ? (
          <>
            <div
              className="absolute inset-0 pointer-events-none"
              aria-hidden
              style={{ background: skin.bg }}
            />
            <SkinPatternOverlay skinId={p.active_skin} />
            <div
              className="absolute inset-0 pointer-events-none opacity-30 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.18),transparent_55%)]"
              aria-hidden
            />
            {/* Skin tag */}
            <span
              className="absolute top-2 right-2 z-20 text-[8px] font-bold uppercase tracking-[0.2em] px-1.5 py-0.5 rounded"
              style={{ backgroundColor: skin.accentSoft, color: skin.text, border: `1px solid ${skin.border}` }}
              data-testid={`social-card-skin-${p.id}`}
            >
              {skin.label}
            </span>
          </>
        ) : (
          <>
            <div
              className="absolute inset-0 pointer-events-none bg-gradient-to-br from-[#0E0E14] via-[#1A1735] to-[#0A0A0A]"
              aria-hidden
            />
            <div
              className="absolute inset-0 pointer-events-none opacity-70 bg-[radial-gradient(ellipse_at_top_right,rgba(124,58,237,0.55),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(0,255,135,0.32),transparent_55%)]"
              aria-hidden
            />
          </>
        )}

        <Link
          to={`${linkPrefix}${encodeURIComponent(p.username)}`}
          className="relative block px-4 pt-4 pb-3"
          data-testid={`social-card-link-${p.id}`}
        >
          {/* TOP STRIP: OVR (reliability) + win-rate badge */}
          <div className="flex items-start justify-between">
            <div className="flex flex-col items-start">
              <span className="font-heading text-[44px] leading-none text-white drop-shadow-[0_3px_8px_rgba(0,0,0,0.7)] tabular-nums">
                {overall}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#00FF87] mt-0.5">
                REL
              </span>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                className="inline-flex items-center gap-1 font-heading text-xs uppercase tracking-wider bg-black/60 border border-[#00FF87]/40 text-[#00FF87] px-2 py-0.5 rounded-sm"
                data-testid={`social-card-winrate-${p.id}`}
              >
                <TrendingUp className="w-3 h-3" /> {p.win_rate ?? 0}%
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/60">
                {(p.platform || "mobile").toUpperCase()}
              </span>
            </div>
          </div>

          {/* Avatar — silhouette (default) or uploaded photo */}
          <div className="relative mx-auto my-3 w-24 h-24 sm:w-28 sm:h-28 flex items-end justify-center">
            {p.avatar_url ? (
              <img
                src={p.avatar_url}
                alt={p.username}
                loading="lazy"
                decoding="async"
                data-testid={`social-card-avatar-${p.id}`}
                className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover ring-2 ring-white/30 shadow-[0_6px_16px_rgba(0,0,0,0.5)]"
              />
            ) : (
              <div
                data-testid={`social-card-silhouette-${p.id}`}
                className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-[#0A0A0A] to-[#1A1735] ring-2 ring-white/10 flex items-center justify-center shadow-[0_6px_16px_rgba(0,0,0,0.6)] overflow-hidden"
                aria-label="no profile picture"
              >
                <UserSilhouette className="w-20 h-20 text-black/85" strokeWidth={2.25} />
              </div>
            )}
          </div>

          {/* Name */}
          <div className="font-heading uppercase text-lg sm:text-xl text-white text-center tracking-wider drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)] line-clamp-1" data-testid={`social-card-name-${p.id}`}>
            {p.username}
          </div>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/55 text-center mt-0.5">
            {club}
          </div>

          {/* Stats strip (FUT footer) */}
          <div className="mt-3 grid grid-cols-3 gap-1 bg-black/55 border border-white/10 rounded-sm px-2 py-2 backdrop-blur-sm">
            <Stat label="PTS" value={p.points ?? 0} accent="text-[#00FF87]" testId={`social-card-pts-${p.id}`} />
            <Stat label="PLD" value={p.played ?? 0} accent="text-white" testId={`social-card-pld-${p.id}`} />
            <Stat label="W-D-L" value={`${p.won ?? 0}-${p.drawn ?? 0}-${p.lost ?? 0}`} accent="text-white/80" small testId={`social-card-wdl-${p.id}`} />
          </div>

          {/* Rails: reliability badge, followers */}
          <div className="mt-2 flex items-center justify-between">
            <ReliabilityBadge score={p.reliability} flagged={p.reliability_flagged} size="sm" testId={`social-card-rel-${p.id}`} />
            <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/65">
              {t("social.followers", { n: p.followers_count ?? 0 })}
            </span>
          </div>
        </Link>

        {/* Action strip */}
        {!isSelf && me && (
          <div className="relative px-4 pb-4 pt-0 flex items-center gap-2">
            <button
              type="button"
              onClick={handleFollow}
              data-testid={`social-card-follow-${p.id}`}
              className={`flex-1 font-mono text-[10px] uppercase tracking-[0.28em] py-2 border transition-all ${
                isFriend
                  ? "border-[#FFD700] text-[#FFD700] bg-black/60"
                  : isFollowing
                    ? "border-[#7C3AED] text-[#7C3AED] bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20"
                    : "border-[#00FF87] text-black bg-[#00FF87] hover:bg-[#5EF0B1]"
              }`}
            >
              {isFriend ? <><Trophy className="inline w-3 h-3 mr-1" /> {t("social.btnFriends")}</>
                : isFollowing ? t("social.btnFollowing")
                : t("social.btnFollow")}
            </button>
            {showMessageButton && isFriend && (
              <button
                type="button"
                onClick={(e) => {
                  // Stop the card's default click handler (which opens the
                  // player profile) so only the drawer opens.
                  e.stopPropagation();
                  e.preventDefault();
                  if (onMessage) {
                    onMessage(p);
                  } else {
                    // Fallback: legacy navigation to the full DM page.
                    window.location.href = `/chat/${p.id}`;
                  }
                }}
                data-testid={`social-card-message-${p.id}`}
                className="font-mono text-[10px] uppercase tracking-[0.28em] py-2 px-3 border border-white/20 text-white hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors"
              >
                {t("social.btnMsg")}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-white", testId, small = false }) {
  return (
    <div className="flex flex-col items-center">
      <span className={`font-score ${small ? "text-sm" : "text-lg"} leading-none tabular-nums ${accent}`} data-testid={testId}>
        {value}
      </span>
      <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-white/50 mt-0.5">{label}</span>
    </div>
  );
}
